import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Psychology,
  Star,
  EmojiEvents,
  Timer,
  Refresh,
  Home,
  VolumeUp,
  VolumeOff,
  Settings as SettingsIcon,
  PlayArrow,
  Info
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import MemoryMatch from './MemoryMatch';

const MemoryGamePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userProfile, updateProfile } = useAuth();
  const [showGame, setShowGame] = useState(false);
  const [gameStats, setGameStats] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);

  // Load user's memory game progress
  const [userStats, setUserStats] = useState(() => {
    const saved = localStorage.getItem('memoryGameStats');
    return saved ? JSON.parse(saved) : {
      gamesPlayed: 0,
      bestScore: 0,
      totalCoins: 0,
      currentStreak: 0,
      bestStreak: 0,
      averageTime: 0,
      levelsCompleted: 0
    };
  });

  // Save stats to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('memoryGameStats', JSON.stringify(userStats));
  }, [userStats]);

  const handleGameComplete = (levelSummary) => {
    const newStats = {
      ...userStats,
      gamesPlayed: userStats.gamesPlayed + 1,
      bestScore: Math.max(userStats.bestScore, levelSummary.score || 0),
      totalCoins: userStats.totalCoins + (levelSummary.coins || 0),
      currentStreak: levelSummary.success ? userStats.currentStreak + 1 : 0,
      bestStreak: Math.max(userStats.bestStreak, levelSummary.success ? userStats.currentStreak + 1 : userStats.currentStreak),
      levelsCompleted: levelSummary.success ? userStats.levelsCompleted + 1 : userStats.levelsCompleted,
      averageTime: userStats.gamesPlayed > 0 
        ? ((userStats.averageTime * userStats.gamesPlayed) + (levelSummary.time || 0)) / (userStats.gamesPlayed + 1)
        : (levelSummary.time || 0)
    };
    
    setUserStats(newStats);
    setGameStats(levelSummary);
  };

  const startGame = () => {
    setShowGame(true);
    setGameStats(null);
  };

  const exitGame = () => {
    setShowGame(false);
  };

  if (showGame) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Game Header */}
        <Box sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <IconButton 
            onClick={exitGame}
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <Home />
          </IconButton>
          
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              color: 'white', 
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
            }}
          >
            🧠 Memory Challenge
          </Typography>
          
          <IconButton 
            onClick={() => setSoundEnabled(!soundEnabled)}
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {soundEnabled ? <VolumeUp /> : <VolumeOff />}
          </IconButton>
        </Box>

        {/* Game Component */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <MemoryMatch 
            initialTheme="neon"
            onLevelComplete={handleGameComplete}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Fade in timeout={800}>
          <Box sx={{ mb: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Zoom in timeout={1000}>
              <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                mb: 2,
                p: 2,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
              }}>
                <Psychology sx={{ 
                  fontSize: { xs: 35, sm: 45 }, 
                  color: 'white', 
                  mr: 1,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                }} />
                <Typography 
                  variant={isMobile ? "h4" : "h3"} 
                  component="h1" 
                  sx={{
                    fontWeight: 800,
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' }
                  }}
                >
                  Memory Training
                </Typography>
              </Box>
            </Zoom>
            
            <Typography 
              variant={isMobile ? "body1" : "h6"} 
              sx={{ 
                color: '#4a5568',
                mb: 4,
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6
              }}
            >
              Boost your cognitive abilities with our scientifically designed memory games
            </Typography>
            
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrow />}
                onClick={startGame}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6c5ce7 100%)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 35px rgba(102, 126, 234, 0.5)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Start Playing
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                startIcon={<Info />}
                onClick={() => setShowInstructions(true)}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.2)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                How to Play
              </Button>
            </Box>
          </Box>
        </Fade>

        {/* Stats Overview */}
        <Fade in timeout={1200}>
          <Grid container spacing={3} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)'
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                  <EmojiEvents sx={{ fontSize: { xs: 30, sm: 40 }, mb: 1 }} />
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}
                  >
                    {userStats.levelsCompleted}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.9,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      fontWeight: 500
                    }}
                  >
                    Levels Completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(240, 147, 251, 0.3)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 40px rgba(240, 147, 251, 0.4)'
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                  <Star sx={{ fontSize: { xs: 30, sm: 40 }, mb: 1 }} />
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}
                  >
                    {userStats.bestScore}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.9,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      fontWeight: 500
                    }}
                  >
                    Best Score
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(79, 172, 254, 0.3)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)'
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                  <Timer sx={{ fontSize: { xs: 30, sm: 40 }, mb: 1 }} />
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}
                  >
                    {Math.round(userStats.averageTime)}s
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.9,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      fontWeight: 500
                    }}
                  >
                    Average Time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(250, 112, 154, 0.3)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 40px rgba(250, 112, 154, 0.4)'
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>🔥</Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}
                  >
                    {userStats.currentStreak}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.9,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      fontWeight: 500
                    }}
                  >
                    Current Streak
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Fade>

        {/* Benefits Section */}
        <Fade in timeout={1400}>
          <Paper sx={{ 
            p: { xs: 3, sm: 4 }, 
            mb: { xs: 3, sm: 4 }, 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 3, 
                textAlign: 'center',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1.3rem', sm: '1.5rem' }
              }}
            >
              🧠 Cognitive Benefits
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 3,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  }
                }}>
                  <Typography variant="h2" sx={{ mb: 2, fontSize: { xs: '2rem', sm: '3rem' } }}>🎯</Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 1,
                      fontWeight: 700,
                      color: '#2d3748',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    Enhanced Focus
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#4a5568',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                  >
                    Improve your ability to concentrate and maintain attention on tasks
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 3,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  }
                }}>
                  <Typography variant="h2" sx={{ mb: 2, fontSize: { xs: '2rem', sm: '3rem' } }}>⚡</Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 1,
                      fontWeight: 700,
                      color: '#2d3748',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    Faster Processing
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#4a5568',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                  >
                    Train your brain to process information more quickly and efficiently
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: 3,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)'
                  }
                }}>
                  <Typography variant="h2" sx={{ mb: 2, fontSize: { xs: '2rem', sm: '3rem' } }}>🧩</Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 1,
                      fontWeight: 700,
                      color: '#2d3748',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    Better Memory
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#4a5568',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                  >
                    Strengthen your working memory and recall abilities
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* Progress Section */}
        {userStats.gamesPlayed > 0 && (
          <Fade in timeout={1600}>
            <Paper sx={{ 
              p: { xs: 3, sm: 4 }, 
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3,
                  fontWeight: 700,
                  color: '#2d3748',
                  fontSize: { xs: '1.3rem', sm: '1.5rem' }
                }}
              >
                📈 Your Progress
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: '#4a5568',
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                  >
                    Memory Mastery
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#667eea',
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                  >
                    {Math.min(100, Math.round((userStats.levelsCompleted / 50) * 100))}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(100, (userStats.levelsCompleted / 50) * 100)} 
                  sx={{ 
                    height: 12, 
                    borderRadius: 6,
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 6
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${userStats.gamesPlayed} Games Played`}
                  sx={{
                    bgcolor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': {
                      bgcolor: '#bbdefb'
                    }
                  }}
                />
                <Chip 
                  label={`Best Streak: ${userStats.bestStreak}`}
                  sx={{
                    bgcolor: '#fce4ec',
                    color: '#c2185b',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': {
                      bgcolor: '#f8bbd9'
                    }
                  }}
                />
                <Chip 
                  label={`${userStats.totalCoins} Coins Earned`}
                  sx={{ 
                    background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)', 
                    color: '#b8860b',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ffed4e 0%, #fff176 100%)'
                    }
                  }}
                />
              </Box>
            </Paper>
          </Fade>
        )}
      </Container>

      {/* Instructions Dialog */}
      <Dialog 
        open={showInstructions} 
        onClose={() => setShowInstructions(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Typography 
            variant="h5" 
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.3rem', sm: '1.5rem' }
            }}
          >
            🧠 How to Play Memory Training
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: '#2d3748',
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              Game Rules:
            </Typography>
            <Box component="ol" sx={{ pl: 2, mb: 3 }}>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                Cards are placed face down in a grid
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                Click two cards to flip them over
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                If they match, they stay face up
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                If they don't match, they flip back over
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                Clear all pairs to complete the level
              </Typography>
            </Box>

            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: '#2d3748',
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              Scoring & Progression:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 3 }}>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                ⭐ Earn stars based on performance
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                🪙 Collect coins for achievements
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                🔥 Build streaks for bonus points
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                🎯 Unlock new themes and levels
              </Typography>
            </Box>

            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: '#2d3748',
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              Tips for Success:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                💡 Try to remember card positions
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                🎯 Start from corners and work inward
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                ⚡ Play regularly to improve memory
              </Typography>
              <Typography 
                component="li" 
                sx={{ 
                  mb: 1,
                  color: '#4a5568',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  lineHeight: 1.6
                }}
              >
                🧘 Stay calm and focused
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setShowInstructions(false)}
            variant="contained"
            sx={{ 
              px: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2
            }}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemoryGamePage;
